import React from 'react';
import { IconButton, Box } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u3_x4_f_k90';

const TEXTS = {
  intro:
    'FK90 Küchenabluftklappen sind spezielle Brandschutzklappen, die in gewerblichen Küchen zum Einsatz kommen. Sie sind für die Abluftleitungen von Küchenabzugshauben konzipiert und verhindern im Brandfall die Ausbreitung von Feuer und Rauch in andere Bereiche des Gebäudes über die Abluftkanäle. Sie sind speziell dafür ausgelegt, die hohen Anforderungen von Küchenabluftsystemen mit Fett- und Rußbelastung zu erfüllen.',
  necessityIntro:
    'FK90 Küchenabluftklappen sind in gewerblichen Küchen unerlässlich, da in Küchen erhöhte Brandgefahren bestehen. Fett und Öle in den Abluftkanälen können sich leicht entzünden und das Feuer über das Abluftsystem verbreiten.',
  necessityMiddle:
    'Die Klappen sind Feuerwiderstandsklasse K90 und Brandschutzklasse EI90 geprüft und müssen in einer Küchenabluftanlage verbaut sein. Die Klappen sorgen dafür das bei einem Brand die Ausbreitung durch die Abluftkanäle in andere Brandabschnitte und Gebäudeteile verhindert wird.',
  necessityLead: 'Die Notwendigkeit von FK90-Klappen resultiert aus:',
  necessityPoints: [
    { label: 'Hohe Brandlast', text: 'Durch Fett, Öl und andere brennbare Stoffe.' },
    { label: 'Schnelle Brandausbreitung', text: 'Entzündetes Fett im Abluftkanal wirkt wie ein Brandbeschleuniger.' },
    { label: 'Schutz vor Rauchgasen', text: 'Verhindern das eindringen von giftigen Rauchgasen in andere Bereiche.' },
    { label: 'Gesetzliche Vorschriften', text: 'In vielen Ländern und Regionen sind FK90-Klappen in Küchenabluftsystemen gesetzlich vorgeschrieben.' }
  ],
  operation:
    'FK90 Küchenabluftklappen arbeiten in der Regel motorisch. Im Normalbetrieb sind sie geöffnet, um den Luftstrom zu gewährleisten. Im Brandfall, ausgelöst durch die Brandmeldeanlage (BMA) oder durch einen thermischen Auslöser im Abluftkanal, schließen sie automatisch. Die Klappen werden durch einen Motor oder eine Federkraft geschlossen und verschließen den Kanal luftdicht. Sie sind Feuerwiderstandsklasse K90 und Brandschutzklasse EI90 geprüft.',
  controlIntro: 'Die Bedieneinheit der FK90-Klappen umfasst folgende Steuerungselemente:',
  controlPoints: [
    { label: 'Netzschalter', text: 'Zum Ein- und Ausschalten der Steuerungseinheit.' },
    { label: 'Betriebsanzeige (LED)', text: 'Zeigt an, ob die Steuerungseinheit eingeschaltet und betriebsbereit ist.' },
    { label: 'Klappe AUF (LED)', text: 'Leuchtet, wenn die Klappe geöffnet ist.' },
    { label: 'Klappe ZU (LED)', text: 'Leuchtet, wenn die Klappe geschlossen ist.' },
    { label: 'Störung (LED)', text: 'Leuchtet bei einer Fehlfunktion der Klappe oder der Steuerung.' },
    { label: 'Testtaste', text: 'Zum Auslösen eines Testlaufs der Klappe.' },
    { label: 'Reset-Taste', text: 'Zum Zurücksetzen der Steuerung nach einem Brandfall oder einer Störung.' },
    { label: 'Zusatz', text: 'Manche Steuerungen besitzen zusätzliche Taster, dies ist von Hersteller zu Hersteller unterschiedlich.' }
  ],
  alarmsIntro:
    'FK90 Küchenabluftklappen verfügen über verschiedene Meldungen und Alarme, die für die Überwachung und Steuerung wichtig sind:',
  alarmsPoints: [
    { label: 'Klappe AUF', text: 'Meldet den geöffneten Zustand der Klappe.' },
    { label: 'Klappe ZU', text: 'Meldet den geschlossenen Zustand der Klappe.' },
    { label: 'Störung', text: 'Meldet eine Fehlfunktion des Motors, der Steuerung oder der Klappe selbst.' },
    { label: 'Thermische Auslösung', text: 'Meldet, wenn die Klappe durch einen thermischen Auslöser geschlossen wurde.' }
  ],
  gltIntro:
    'Die Anbindung der FK90-Klappen an die Gebäudeleittechnik (GLT) ist in der Regel über potentialfreie Kontakte realisiert. Folgende Meldungen werden übertragen:',
  gltPoints: [
    { label: 'Klappe AUF', text: 'Überträgt den geöffneten Zustand.' },
    { label: 'Klappe ZU', text: 'Überträgt den geschlossenen Zustand.' },
    { label: 'Störung', text: 'Signalisiert eine Fehlfunktion.' },
    { label: 'Thermische Auslösung', text: 'Signalisiert eine thermische Auslösung.' }
  ],
  gltMonitoring:
    'Die Überwachung der Kontakte in der GLT erfolgt auf Drahtbruch. Dies bedeutet, dass sowohl eine offene als auch eine geschlossene Klappe als aktiver Zustand erkannt werden können. Sollte es zu einem Drahtbruch kommen, wird dies als Störung gemeldet. So ist sichergestellt, dass bei einer Störung, die Lüftungsanlage ausser Betrieb geht.',
  gltResponse:
    'Die GLT kann auf diese Meldungen reagieren, Alarme auslösen und den Betriebszustand der Klappen anzeigen. In der GLT ist ein Notlauf hinterlegt der im Fehlerfall aktiviert wird. Des weiteren wird dort die Brandfallmatrix berücksichtigt.',
  maintenanceIntro:
    'Wartung: FK90-Klappen müssen regelmäßig auf ihre Funktionstüchtigkeit geprüft und gewartet werden. Die Wartung sollte durch Fachpersonal erfolgen und umfasst:',
  maintenancePoints: [
    { label: '', text: 'Sichtkontrolle auf Beschädigungen.' },
    { label: '', text: 'Prüfung der Beweglichkeit der Klappe.' },
    { label: '', text: 'Test der elektrischen Komponenten.' },
    { label: '', text: 'Kontrolle der Steuerung und der Meldungen.' },
    { label: '', text: 'ggf. Reinigung der Klappe und der Umgebung.' }
  ]
};

const bulletToText = (items: Array<{ label: string; text: string }>) =>
  items.map(i => (i.label ? `${i.label}: ${i.text}` : i.text));

const textBlocks = [
  { id: 'block_intro', heading: 'Einführung', text: TEXTS.intro },
  {
    id: 'block_notwendigkeit',
    heading: 'Notwendigkeit',
    text:
      TEXTS.necessityIntro + ' ' +
      TEXTS.necessityMiddle + ' ' +
      TEXTS.necessityLead + ' ' + bulletToText(TEXTS.necessityPoints).join(' ')
  },
  { id: 'block_betrieb', heading: 'Betrieb', text: TEXTS.operation },
  {
    id: 'block_steuerung',
    heading: 'Steuerungselemente',
    text: TEXTS.controlIntro + ' ' + bulletToText(TEXTS.controlPoints).join(' ')
  },
  {
    id: 'block_meldungen',
    heading: 'Meldungen und Alarme',
    text: TEXTS.alarmsIntro + ' ' + bulletToText(TEXTS.alarmsPoints).join(' ')
  },
  {
    id: 'block_glt',
    heading: 'GLT Anbindung',
    text:
      TEXTS.gltIntro + ' ' + bulletToText(TEXTS.gltPoints).join(' ') + ' ' + TEXTS.gltMonitoring + ' ' + TEXTS.gltResponse
  },
  {
    id: 'block_wartung',
    heading: 'Wartung',
    text: TEXTS.maintenanceIntro + ' ' + bulletToText(TEXTS.maintenancePoints).join(' ')
  }
];

const Page: React.FC = () => {
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const canEditTexts = canEditTextsM1;
  const canUploadImages = canUploadImagesM1;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const blocks = React.useMemo(() => {
    return textBlocks.map(b => ({
      ...b,
      text: override?.texts?.[b.id] ?? b.text
    }));
  }, [override]);

  const fullText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.text}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const imgSrc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const handleImageReplace = React.useCallback(
    async (key: string, file: File) => {
      if (!token) return alert('Kein Token.');
      if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
      try {
        await uploadUnitAsset(PAGE_ID, key, file, token);
        alert('Bild aktualisiert.');
        refresh();
      } catch (e: any) {
        alert('Upload fehlgeschlagen: ' + (e?.message || e));
      }
    },
    [token, refresh, canUploadImages]
  );

  const handleTextSave = React.useCallback(
    async (blockId: string, body: string) => {
      if (!token) return alert('Kein Token.');
      if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
      try {
        await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
        alert('Text gespeichert.');
        refresh();
      } catch (e: any) {
        alert('Speichern fehlgeschlagen: ' + (e?.message || e));
      }
    },
    [token, refresh, canEditTexts]
  );

  const actions = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '1.3.4 FK90', actions },
    React.createElement(
      Box,
      { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      // Bild-Spalte
      React.createElement(
        Box,
        { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(ZoomableImage, {
          src: imgSrc('img_0', '/assets/drawable/bsk_fk90.jpeg'),
          alt: 'FK90 Küchenabluftklappe',
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_0', f) : undefined
        }),
        React.createElement(ZoomableImage, {
          src: imgSrc('img_1', '/assets/drawable/bsk_fk90_3.png'),
          alt: 'Ansicht FK90',
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_1', f) : undefined
        }),
        React.createElement(ZoomableImage, {
          src: imgSrc('img_2', '/assets/drawable/bsk_fk90_2.jpeg'),
          alt: 'Detail FK90',
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_2', f) : undefined
        }),
        React.createElement(ZoomableImage, {
          src: imgSrc('img_3', '/assets/drawable/bsk_fk90_4.jpeg'),
          alt: 'Steuerung FK90',
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_3', f) : undefined
        }),
        React.createElement(ZoomableImage, {
          src: imgSrc('img_4', '/assets/drawable/bsk_fk90_6.jpeg'),
          alt: 'GLT Anbindung',
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_4', f) : undefined
        })
      ),
      // Text-Spalte
      React.createElement(
        Box,
        { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        blocks.map(b =>
          React.createElement(EditableTextBlock, {
            key: b.id,
            pageId: PAGE_ID,
            blockId: b.id,
            heading: b.heading,
            body: b.text,
            onSave: (body: string) => handleTextSave(b.id, body)
          })
        )
      )
    )
  );
};

export default Page;

